import React, { Suspense, lazy, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { AppContext } from '../context/AppContext';
import { useOptionalTenantIdentity } from '../context/TenantIdentityContext';
import { useOptionalSiteSettings } from '../context/SiteSettingsContext';
import { trackEvent } from '../utils/analytics';
import { SECTION_NAV_ITEMS, isPmPartnersEnabled, shouldShowHeaderSectionNav } from '../config/sectionNav';
import { getSupportPhone } from '../utils/envUtils';
import { APP_LOGO_SOURCES } from '../utils/logoAssetVersion';
import '../styles/headerReskin.css';
import '../styles/navBar.css';

const UserProfile = lazy(() => import('./auth/UserProfile'));

const ROUTE_TAGLINES = {
  '/': 'We Park with Care',
  '/truck-parking': 'Find Open Spots Fast',
  '/rates': 'Simple Rates. No Surprises.',
  '/about': 'Built for Drivers',
  '/terms-and-conditions': 'Parking Terms & Conditions',
  '/privacy-policy': 'Privacy Policy',
  '/pm-partners': 'Trusted Roadside Partners',
  '/hiring': 'We Hire Drivers',
  '/truck-repair-and-maintenance': 'Truck Repair + PM Workflows',
  '/truck-parking/last-hour-parking': 'Last-Hour Truck Parking',
};

const getAdminTagline = (pathname = '', search = '') => {
  if (/^\/admin\/report(?:\/|$)/i.test(pathname)) return 'Financial Reports';
  if (!/^\/admin(?:\/|$)/i.test(pathname)) return null;

  const activeTab = new URLSearchParams(search).get('tab') || 'operations';
  if (/^\/admin\/hiring(?:\/|$)/i.test(pathname)) {
    const hiringLabels = {
      operations: 'Hiring Operations',
      analytics: 'Hiring Analytics',
      tools: 'Hiring Tools',
    };
    return hiringLabels[activeTab] || hiringLabels.operations;
  }

  const adminLabels = {
    operations: 'Parking Operations',
    blog: 'Blog Studio',
    stats: 'Financial Analytics',
    ads: 'Advertising',
    'short-urls': 'Short URLs',
    emails: 'Email Analytics',
    controls: 'Site Controls',
  };

  return adminLabels[activeTab] || 'Control Center';
};

const getRouteTagline = (pathname = '', search = '') => {
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '') || '/';

  const adminTagline = getAdminTagline(normalizedPath, search);
  if (adminTagline) return adminTagline;

  if (/^\/manage-booking(?:\/|$)/i.test(normalizedPath)) return 'Manage Your Booking';
  if (/^\/tenant(?:\/|$)/i.test(normalizedPath)) return 'Tenant Operations';
  if (/^\/spot-changes(?:\/|$)/i.test(normalizedPath)) return 'Manager Console';
  if (/^\/pricing(?:\/|$)/i.test(normalizedPath)) return 'Choose Your Parking Plan';
  if (/^\/pm-partners\/[^/]+/i.test(normalizedPath)) return 'Partner Services Near You';
  if (/^\/truck-repair-and-maintenance\/[^/]+/i.test(normalizedPath)) return 'Repair Coordination Intelligence';
  if (/^\/hiring\/otr(?:\/|$)/i.test(normalizedPath)) return 'OTR Driver Hiring';
  if (/^\/hiring\/box-truck(?:\/|$)/i.test(normalizedPath)) return 'Box Truck Driver Hiring';
  if (/^\/hiring\/oversize(?:\/|$)/i.test(normalizedPath)) return 'Oversize Driver Hiring';
  if (/^\/hiring\/amazon-relay(?:\/|$)/i.test(normalizedPath)) return 'Amazon Relay Hiring';

  return ROUTE_TAGLINES[normalizedPath] || null;
};

const isHiringRoute = (pathname = '') => /^\/(?:admin\/)?hiring(?:\/|$)/i.test(pathname);

const normalizePhoneHref = (phone) => {
  if (!phone) return '';
  const trimmed = String(phone).trim();
  if (trimmed.startsWith('+')) return `tel:${trimmed.replace(/[^\d+]/g, '')}`;
  return `tel:${trimmed.replace(/\D/g, '')}`;
};

const NavBar = ({ showUserProfile = true }) => {
  const location = useLocation();
  const siteSettingsContext = useOptionalSiteSettings();
  const siteBranding = siteSettingsContext?.settings?.branding || {};
  const appContext = useContext(AppContext) || {};
  const tenantIdentity = useOptionalTenantIdentity();
  const bookingSubmitted = Boolean(appContext.bookingSubmitted);
  const setBookingSubmitted = typeof appContext.setBookingSubmitted === 'function'
    ? appContext.setBookingSubmitted
    : null;
  const addresses = Array.isArray(appContext.addresses) ? appContext.addresses : [];
  const lotsAvailableByLocationId = appContext.lotsAvailableByLocationId || {};
  const liveOpenSpotsCount = addresses.reduce((total, addr) => (
    total + (lotsAvailableByLocationId?.[addr?.id]?.length || 0)
  ), 0);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isTenantWorkspace = /^\/tenant(?:\/|$)/i.test(location.pathname);
  const isAdminWorkspace = /^\/admin(?:\/|$)/i.test(location.pathname);
  const showPrimaryNav = !isTenantWorkspace && shouldShowHeaderSectionNav(location.pathname);
  const isHiring = isHiringRoute(location.pathname);
  const routeTagline = getRouteTagline(location.pathname, location.search);
  const taglineText = isHiring && !isAdminWorkspace ? 'We Hire with Care' : (routeTagline || 'We Park with Care');
  const parkingBrandPrimaryText = siteBranding.headerBrandPrimaryText || 'Truck Parking';
  const parkingBrandSecondaryText = siteBranding.headerBrandSecondaryText || 'Hub';
  const parkingLogoUrl = siteBranding.headerLogoUrl || '';
  const parkingLogoAltText = siteBranding.headerLogoAltText || 'Truck Parking Hub logo';
  const brandLabel = isHiring ? 'Elis ProHire' : parkingBrandPrimaryText;
  const brandHomeHref = isHiring ? '/hiring' : '/';
  const brandHomeLabel = isHiring ? 'Elis ProHire home' : 'Homepage';
  const supportPhone = getSupportPhone();
  const supportPhoneHref = normalizePhoneHref(supportPhone);
  const tenantSessionActive = tenantIdentity?.isAuthenticated === true;
  const tenantSessionLoading = tenantIdentity?.status === 'loading';
  const tenantCompanyName = tenantIdentity?.session?.tenant?.companyName || 'Owner workspace';
  const showOwnerWorkspaceSwitcher = !isTenantWorkspace && !isAdminWorkspace && tenantSessionActive;
  const showAuth0Account = !isTenantWorkspace && (
    isAdminWorkspace || (!tenantSessionActive && !tenantSessionLoading)
  );

  const primaryLinks = SECTION_NAV_ITEMS.filter(
    (item) => item.showInHeaderStrip && (item.sectionKey !== 'pm_partners' || isPmPartnersEnabled())
  );

  const dismissBookingConfirmation = useCallback(() => {
    if (bookingSubmitted && setBookingSubmitted) {
      setBookingSubmitted(false);
    }
  }, [bookingSubmitted, setBookingSubmitted]);

  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);

  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.style.overflow = isDrawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    if (!isDrawerOpen || typeof document === 'undefined') return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeDrawer();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, closeDrawer]);

  const handleNavLinkClick = (section) => {
    dismissBookingConfirmation();
    trackEvent('nav_click', {
      source: 'nav_bar',
      section: section.analyticsKey,
      sectionKey: section.sectionKey,
    });
  };

  const handleLogoClick = () => {
    dismissBookingConfirmation();
  };

  const logoContent = (
    <>
      {isHiring ? (
        <img src="/elisd-logo.png" alt="Elis ProHire logo" width="44" height="44" decoding="async" className="tph-nav-logo-img" />
      ) : parkingLogoUrl ? (
        <img src={parkingLogoUrl} alt={parkingLogoAltText} width="44" height="44" decoding="async" className="tph-nav-logo-img" />
      ) : (
        <picture>
          <source media="(max-width: 767px)" srcSet={APP_LOGO_SOURCES.mobile.avif} type="image/avif" />
          <source media="(max-width: 767px)" srcSet={APP_LOGO_SOURCES.mobile.webp} type="image/webp" />
          <source media="(min-width: 768px)" srcSet={APP_LOGO_SOURCES.desktop.avif} type="image/avif" />
          <source media="(min-width: 768px)" srcSet={APP_LOGO_SOURCES.desktop.webp} type="image/webp" />
          <img src={APP_LOGO_SOURCES.fallback} alt={parkingLogoAltText} width="44" height="44" decoding="async" className="tph-nav-logo-img" />
        </picture>
      )}
      <span className="tph-nav-logo-text">
        <span className="tph-nav-logo-eyebrow tph-nav-slide-left" key={`eyebrow-${location.pathname}`}>
          {brandLabel}
        </span>
        {!isHiring && (
          <span className="tph-nav-logo-hub tph-nav-slide-left" key={`hub-${location.pathname}`} style={{ color: 'var(--hub-color, #E10600)' }}>
            {parkingBrandSecondaryText}
          </span>
        )}
        {isHiring && (
          <span className="tph-nav-logo-hub tph-nav-slide-left" key={`hub-${location.pathname}`}>
            {brandLabel}
          </span>
        )}
      </span>
    </>
  );

  const logoLockup = isTenantWorkspace ? (
    <a href={brandHomeHref} onClick={handleLogoClick} aria-label={brandHomeLabel} className="tph-nav-logo">
      {logoContent}
    </a>
  ) : (
    <Link to={brandHomeHref} onClick={handleLogoClick} aria-label={brandHomeLabel} className="tph-nav-logo">
      {logoContent}
    </Link>
  );

  return (
    <header className="tph-nav" data-testid="header">
      <div className="tph-nav-bar">
        {logoLockup}

        {taglineText && (
          <span
            className="tph-nav-tagline tph-nav-slide-right"
            key={`tagline-${location.pathname}`}
            aria-hidden="true"
            data-testid="nav-tagline"
          >
            {taglineText}
          </span>
        )}

        {showPrimaryNav && (
          <nav className="tph-nav-links" aria-label="Primary" data-testid="top-nav-bar">
            {primaryLinks.map((section) => (
              <NavLink
                key={section.sectionKey}
                to={section.path}
                end={section.path === '/'}
                onClick={() => handleNavLinkClick(section)}
                className={({ isActive }) => `tph-nav-link${isActive ? ' is-active' : ''}`}
                data-testid={`top-nav-link-${section.sectionKey}`}
              >
                {section.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="tph-nav-right">
          {isTenantWorkspace && (
            <a className="tph-nav-tenant-exit" href="/">
              <ArrowLeftIcon aria-hidden="true" />
              Back to site
            </a>
          )}
          {showUserProfile && showOwnerWorkspaceSwitcher && (
            <Link className="tph-nav-owner-workspace" to="/tenant" aria-label="Open owner workspace">
              <BuildingStorefrontIcon aria-hidden="true" />
              <span><strong>Owner workspace</strong><small>{tenantCompanyName}</small></span>
            </Link>
          )}
          {showUserProfile && showAuth0Account && (
            <div className="tph-nav-profile" data-testid="top-nav-user-profile">
              <Suspense fallback={null}>
                <UserProfile showSignInLabel signInLinksToPage />
              </Suspense>
            </div>
          )}

          {!isTenantWorkspace && (
            <button
              type="button"
              className="tph-nav-burger tph-nav-iconbtn"
              aria-label="Menu"
              aria-expanded={isDrawerOpen}
              onClick={openDrawer}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {isDrawerOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="tph-nav-drawer is-open"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          data-testid="nav-drawer"
        >
          <div className="tph-nav-drawer-head">
            {logoLockup}
            <button type="button" className="tph-nav-iconbtn" aria-label="Close menu" onClick={closeDrawer}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <line x1="5" y1="5" x2="19" y2="19" />
                <line x1="19" y1="5" x2="5" y2="19" />
              </svg>
            </button>
          </div>
          {showUserProfile && showOwnerWorkspaceSwitcher && (
            <div className="tph-nav-drawer-owner-session">
              <Link to="/tenant" onClick={closeDrawer} aria-label="Open owner workspace">
                <BuildingStorefrontIcon aria-hidden="true" />
                <span><strong>Owner workspace</strong><small>{tenantCompanyName}</small></span>
              </Link>
            </div>
          )}
          {showUserProfile && showAuth0Account && (
            <div className="tph-nav-drawer-account-panel" data-testid="drawer-user-profile">
              <Suspense fallback={null}>
                <UserProfile inline showSignInLabel signInLinksToPage hideWhenSignedOut />
              </Suspense>
            </div>
          )}
          {showPrimaryNav && (
            <nav aria-label="Primary">
              {primaryLinks.map((section) => (
                <NavLink
                  key={section.sectionKey}
                  to={section.path}
                  end={section.path === '/'}
                  onClick={() => handleNavLinkClick(section)}
                  className={({ isActive }) => `tph-nav-drawer-link${isActive ? ' is-active' : ''}`}
                >
                  {section.label}
                </NavLink>
              ))}
            </nav>
          )}
          <div className="tph-nav-drawer-actions">
            {showUserProfile && showAuth0Account && (
              <div className="tph-nav-drawer-signin" data-testid="drawer-signin-profile">
                <Suspense fallback={null}>
                  <UserProfile inline showSignInLabel signInLinksToPage signedOutOnly />
                </Suspense>
              </div>
            )}

            {supportPhoneHref && (
              <a className="tph-nav-support tph-nav-support--drawer" href={supportPhoneHref}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.9.6 2.8.7a2 2 0 0 1 1.7 2z" />
                </svg>
                Support · {supportPhone}
              </a>
            )}

            {liveOpenSpotsCount > 0 && (
              <div className="tph-nav-drawer-live" data-testid="drawer-live-spots">
                <span className="tph-nav-drawer-live-dot" aria-hidden="true" />
                {liveOpenSpotsCount} spots available now
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default NavBar;
